import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, Button, Popconfirm, message } from 'antd';
import { connect } from 'react-redux';
import { Redirect, Route, withRouter } from 'react-router';
import RouteWithSubRoutes from '../route_with_sub_routes/RouteWithSubRoutes';
import SubnavTabs from '../subnav_tabs/SubnavTabs';
import { getEntityById, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	getProtectedImageUriForSupplier,
	getProtectedImageUriFromString,
} from '../../utils/FileAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import Carousel, { Modal as Lightbox, ModalGateway } from 'react-images';
import { generateBackLink, getBackLinkStates } from '../../utils/HistoryUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { Link } from 'react-router-dom';
import { contractorLocationsRestCrudThunksForSupplier } from '../../thunks/contractor_locations_thunks';
import { updateNavbarState } from '../../actions/navbar_actions';

require('../supplier_locations_detail_page/SupplierLocationsDetailPage.less');

const { Content } = Layout;

const ContractorLocationDetailPage: FC<any> = ({
	match,
	history,
	contractorLocation,
	getLocation,
	childRoutes,
	fetching,
	deleteLocation,
}): React.ReactElement => {
	const defaultPath = `/supplier/contractor_clients/overview/all`;

	const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [deleting, setDeleting] = useState(false);

	const locationId = match.params.id;

	useEffect(() => {
		if (!nullSafeGet('id', contractorLocation)) {
			getLocation(locationId);
		}
	}, []);

	const canDelete = useMemo(
		() => !fetching && !nullSafeGet('contractorTenant.connectedLocationId', contractorLocation),
		[contractorLocation, fetching]
	);

	const _handleViewChange = (idx) => setCurrentIndex(idx);

	const _openLightbox = (currentIndex = 0) => {
		setLightboxIsOpen(true);
		setCurrentIndex(currentIndex);
	};

	const _closeLightbox = () => setLightboxIsOpen(false);

	// useEffect(() => {
	// 	updateNavbar(
	// 		'Back to Locations',
	// 		generateBackLink('/supplier/contractor_locations/overview/all', location)
	// 	);
	// }, []);

	const handleTabChange = (key) => history.push(`/supplier/locations/detail/${locationId}/${key}`);

	const checkTabActive = (key) => {
		const pathname = history.location.pathname;
		return pathname.indexOf(key) !== -1;
	};

	const tabs = [
		{
			key: 'details',
			name: 'Overview',
		},
	];

	const photoUris = useMemo(
		() =>
			nullSafeGetOrElse('images', contractorLocation, [])
				.slice(0, 4)
				.map((locImg) => {
					const [imgFileId, imgFileName] = locImg.split('/');
					return getProtectedImageUriForSupplier(imgFileId, imgFileName, 1200, 800);
				}),
		[]
	);

	const lightboxPhotoUris = useMemo(
		() =>
			nullSafeGetOrElse('images', contractorLocation, []).map((photoString) => ({
				src: getProtectedImageUriFromString(ROLE_TYPES.SUPPLIER)(photoString),
			})),
		[]
	);

	const onDelete = useCallback(() => {
		setDeleting(true);
		deleteLocation(contractorLocation)
			.then(() => {
				message.success('location deleted successfully!');
				history.replace('/supplier/contractor_clients/overview/locations');
			})
			.catch(message.error)
			.finally(() => setDeleting(false));
	}, [contractorLocation, deleteLocation, history]);

	return (
		<div>
			<Route
				path="/supplier/contractor_clients/locations/detail/:id"
				exact={true}
				render={(props) => (
					<Redirect
						to={{
							pathname: `/supplier/contractor_clients/locations/detail/${locationId}/details`,
						}}
					/>
				)}
			/>
			<Content className="supplierLocationsDetailPage">
				<ModalGateway>
					{lightboxIsOpen ? (
						<Lightbox onClose={_closeLightbox}>
							<Carousel
								trackProps={{
									onViewChange: _handleViewChange,
								}}
								views={lightboxPhotoUris}
								currentIndex={currentIndex}
							/>
						</Lightbox>
					) : null}
				</ModalGateway>

				<div className="imageLayout">
					{photoUris.map((photoUri, idx) => (
						<div
							key={idx}
							className={`imageTile imageTile${idx}`}
							style={{ backgroundImage: `url(${photoUri})` }}
						/>
					))}
					<div
						className={`imageLayout__gradientOverlay ${
							contractorLocation.isActive ? '' : 'imageLayout__gradientOverlay--darker'
						}`}
					>
						{!contractorLocation.isActive ? (
							<div className="locationInactiveLabel">Inactive</div>
						) : null}
						<div className="supplierLocationsDetailPage__overtitle">
							<div className="flex flex-row items-baseline">
								{nullSafeGet('contractorClient.name', contractorLocation)}
								{nullSafeGet('storeId', contractorLocation)
									? `${nullSafeGet('storeId', contractorLocation)}`
									: null}
								<div className="ml-2">
									{nullSafeGet('contractorTenant.connectedLocation', contractorLocation)
										? '(Customer Managed)'
										: '(Self Managed)'}
								</div>
							</div>
						</div>
						<div className="flex flex-col text-white">
							<h4 className="supplierLocationsDetailPage__title">{contractorLocation.name}</h4>
						</div>
						<SubnavTabs tabs={tabs} onChange={handleTabChange} isActive={checkTabActive} />
						<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_LOCATIONS}>
							<div style={{ position: 'absolute', top: 40, right: 40 }}>
								<div className="itmes-center flex flex-row">
									<Link
										to={`/supplier/contractor_clients/locations/edit/${locationId}`}
										style={{ marginRight: 16 }}
									>
										<Button ghost={true}>Edit</Button>
									</Link>
									{canDelete ? (
										<Popconfirm
											onConfirm={onDelete}
											placement="topLeft"
											title="Are you sure you want to delete this location?"
											okText="Yes"
											cancelText="No"
										>
											<Button
												loading={deleting}
												ghost={true}
												style={{ marginBottom: 16, marginRight: 16 }}
												key={'Delete'}
											>
												Delete
											</Button>
										</Popconfirm>
									) : null}
								</div>
							</div>
						</AccessPermissionChecker>
						<div
							style={{ position: 'absolute', bottom: 40, right: 40 }}
							className="flex flex-row items-baseline"
						>
							{lightboxPhotoUris.length > 0 ? (
								<Button
									style={{ color: 'white', marginRight: 16 }}
									type="ghost"
									onClick={() => _openLightbox()}
								>
									View Photos
								</Button>
							) : null}
						</div>
					</div>
				</div>
				{childRoutes.map((r, i) => (
					<RouteWithSubRoutes key={i} {...r} />
				))}
			</Content>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	fetching: state.contractor_locations.fetching,
	contractorLocation: getEntityById(ownProps.match.params.id, state.contractor_locations),
});

const mapDispatchToProps = (dispatch) => ({
	getLocation: (id) => dispatch(contractorLocationsRestCrudThunksForSupplier.readOne(id)),
	updateNavbar: (backlinkTitle, backlinkTo, navbarBackgroundColor) =>
		dispatch(updateNavbarState(backlinkTitle, backlinkTo, navbarBackgroundColor)),
	deleteLocation: (entity) =>
		dispatch(contractorLocationsRestCrudThunksForSupplier.delete(entity, 'id', null, false, true)),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(ContractorLocationDetailPage)
);
